import { convertToISODateTimeString, DATE_FORMAT } from '@/helpers/dates';
import { debounceAsync } from '@/helpers/debounceAsync';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { useGetAccountQuery } from '@/services/accounts';
import { isErrorWithData } from '@/services/helpers';
import {
  useSaveInstallEnergyBackupPackageMutation,
  useSubmitInstallEnergyBackupPackageMutation,
} from '@/services/installEnergyBackupPackage';
import { useSearchProductsMutation } from '@/services/productCatalog';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { ReviewPackageActions } from '@/types/ReviewPackage';
import { Alert, Box, Button, FormikTextInput, TextLink, toast } from '@palmetto/palmetto-components';
import { Field, FieldArray, Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  DocumentTypes,
  EnergyBackupExistingSystemFinanceType,
  EnergyBackupSystem,
  EquipmentType,
  EquipmentTypeLabels,
  InstallationPackagePhotoCategory,
  InstallDocumentTypes,
  MilestoneType,
  ProgramType,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsType,
  UserPermissions,
  validateMonitoringIdFormat,
  VerifyExistingSystemDocumentTypes,
} from 'types';

import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import { AsyncSelectInput } from '../Forms/AsyncSelect';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { ApprovedInterconnectionAgreement } from '../MilestonePackagePartials/ApprovedInterconnectionAgreement';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';
import { PhotosList } from '../MilestonePackagePartials/Documents/PhotosList';
import { Footer } from '../MilestonePackagePartials/Footer';
import { Permit } from '../MilestonePackagePartials/Permit';
import { permitDateFields } from '../MilestonePackagePartials/permitDateFields';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { UtilityBill } from '../MilestonePackagePartials/UtilityBill';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

import { applyModelSelections, buildModelSelections, filterProductsForModelSelection } from './modelSelectionUtils';

const FINANCE_TYPE_LABEL: Record<EnergyBackupExistingSystemFinanceType, string> = {
  [EnergyBackupExistingSystemFinanceType.cash]: 'Cash',
  [EnergyBackupExistingSystemFinanceType.leasePPA]: 'Lease/PPA',
  [EnergyBackupExistingSystemFinanceType.loan]: 'Loan',
  [EnergyBackupExistingSystemFinanceType.unknown]: 'Unknown',
} as const;

export const formatInstallPackageData = (values: FormikValues, originalEquipment?: EnergyBackupSystem[]) => {
  // Apply model selections to the original equipment structure
  // This preserves the original structure while incorporating user-selected modelNumbers
  const equipment =
    originalEquipment && values.modelSelections
      ? applyModelSelections(originalEquipment, values.modelSelections)
      : values.systemDesign?.equipment;

  return {
    systemDesign: {
      ...values.systemDesign,
      equipment,
    },
    monitoring: {
      batteryMonitoringSiteId: values.batteryMonitoringSiteId,
    },
    permitSubmittedDate: {
      palmetto: values.permitSubmittedDate ? convertToISODateTimeString(values.permitSubmittedDate) : undefined,
    },
    permitApprovedDate: {
      palmetto: values.permitApprovedDate ? convertToISODateTimeString(values.permitApprovedDate) : undefined,
    },
    installScheduledDate: {
      palmetto: values.installScheduledDate ? convertToISODateTimeString(values.installScheduledDate) : undefined,
    },
    scheduledInspectionDate: {
      palmetto: values.scheduledInspectionDate
        ? convertToISODateTimeString(values.scheduledInspectionDate)
        : undefined,
    },
    expectedPTODate: {
      palmetto: values.expectedPTODate ? convertToISODateTimeString(values.expectedPTODate) : undefined,
    },
    utility: {
      tariffId: values.tariffId === '' ? null : values.tariffId,
    },
  };
};

export const energyBackupInstallPackageSchema = yup.object({
  ...permitDateFields,
  scheduledInspectionDate: yup
    .string()
    .required('Scheduled Inspection Date is a required field.')
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Scheduled Inspection Date must be in MM-DD-YYYY format.'),
  expectedPTODate: yup
    .string()
    .required('Expected PTO Date is a required field.')
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Expected PTO Date must be in MM-DD-YYYY format.'),
});

const checkboxCategories = [
  InstallDocumentTypes.permit,
  DocumentTypes.utilityBill,
  InstallDocumentTypes.incentiveDocuments,
  InstallDocumentTypes.planSet,
  InstallDocumentTypes.energyBackupInstallationPhotos,
];

export const InstallEnergyBackupMilestonePackage = ({
  canSubmitInstallPackage,
}: {
  canSubmitInstallPackage: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { claims } = useAuth();
  const permissions = usePermissions();
  const isFinCoUser = isInternalUser(claims, true);
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);

  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.installPackage,
  });
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const [triggerSearch] = useSearchProductsMutation();
  const [triggerSubmit] = useSubmitInstallEnergyBackupPackageMutation();
  const [triggerSave] = useSaveInstallEnergyBackupPackageMutation();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const triggerToastSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.install);

  const allowsAction = useIsOrgReadOnly();

  const isLoading = isAccountLoading || isQueueLoading;
  const canSubmitReview = action === ReviewPackageActions.review && queue;

  const queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser: boolean =
    !isFinCoUser &&
    queue &&
    (queue.status === QueueStatus.conditionallyApproved || queue.status === QueueStatus.approved);

  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });

  useEffect(() => {
    if (!action || !ReviewPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (
      account &&
      account?.existingSystemDetails === undefined &&
      location.state?.fromVerifyExistingSystem !== true
    ) {
      navigate(`/accounts/${id}/verify-existing-system`, { replace: true });
    }
    if (action === ReviewPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/installation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/installation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue, account, location]);

  const handleValidation = (values: any) => {
    const errors = {} as any;
    const batteryManufacturer = values.systemDesign?.equipment?.[0]?.manufacturer;
    const batteryMonitoringIdFormat = validateMonitoringIdFormat(
      values.batteryMonitoringSiteId,
      batteryManufacturer,
    );
    if (values.batteryMonitoringSiteId && !batteryMonitoringIdFormat.valid) {
      errors.batteryMonitoringSiteId = `Monitoring Site ID must match the required format: ${batteryMonitoringIdFormat.expectedFormat}.`;
    }

    return errors;
  };

  const handleSave = async ({ data }: FormikValues) => {
    setIsSaving(true);
    // Pass original equipment to prevent flattened data from corrupting the system design
    const formattedData = formatInstallPackageData(data, account?.systemDesign?.equipment);
    try {
      await triggerSave({ accountId: id, savePackage: formattedData }).unwrap();
      triggerToastSuccessMessage();
    } catch (error) {
      console.error('Error saving installation package:', error);
      if (isErrorWithData(error)) {
        const errorMessage = error.data.message;
        throw new Error(errorMessage);
      } else {
        throw new Error('Error saving Install Package');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    try {
      // Pass original equipment to prevent flattened data from corrupting the system design
      const formattedData = formatInstallPackageData(values, account?.systemDesign?.equipment) as any;
      await triggerSubmit({
        accountId: id,
        installPackage: {
          ...formattedData,
        },
      }).unwrap();
      toast.success('Install Package Submitted');
      navigate(`/accounts/${id}`);
    } catch (error: any) {
      console.error('Error submitting installation package:', error);
      if (isErrorWithData(error)) {
        const errorMessage = error.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Error submitting Install Package');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const modelSelections = useMemo(() => {
    if (!account) {
      return [];
    }
    return buildModelSelections(account?.systemDesign?.equipment || []);
  }, [account]);

  const handleSearchText = async (searchText: string, name: string, manufacturer: string) => {
    const { data, error } = await triggerSearch({
      ...(searchText ? { query: searchText } : {}),
      limit: 20,
      page: 1,
      filters: [
        {
          filter: 'classificationPath',
          operation: {
            equals: 'solar/energy-storage/backup',
          },
        },
        {
          filter: 'attribute',
          key: 'lightreach-energy-backup-enabled',
          operation: {
            equals: true,
          },
        },
        {
          filter: 'availableOnDate',
          operation: {
            equals: new Date().toISOString(),
          },
        },
      ],
    });
    if (error) {
      toast.error('Error searching products. Please try again.');
      return undefined;
    }

    const productLineOptions = filterProductsForModelSelection(data?.data || [], manufacturer, name);

    const productData = productLineOptions?.map((product) => {
      const model = product.attributes.find((attr) => attr.key === 'model-number')?.value ?? '';
      const productLine =
        product.attributes.find((attr) => attr.key === 'lightreach-product-line')?.value ?? ('' as EquipmentType);
      return {
        label: `${product.manufacturer} - ${model}`,
        value: product.id,
        manufacturer: product.manufacturer,
        model,
        name: productLine,
      };
    });
    return productData?.length && (data?.paging?.totalPages || 0) > 1
      ? [
          {
            label: `${data?.paging.totalRecords} results found. Continue typing to narrow down results.`,
            value: undefined,
          },
          ...productData,
        ]
      : productData;
  };

  const debouncedSearch = debounceAsync(
    (value: string, name: string, manufacturer: string) => handleSearchText(value, name, manufacturer),
    400,
    2,
    true,
  );

  const initialValues = {
    systemDesign: account.systemDesign,
    modelSelections,
    lseId: account?.utility?.lseId,
    state: account?.address?.state,
    tariffId: account?.utility?.tariffId || '',

    batteryMonitoringSiteId: account.batteryMonitoringSiteId,
    permitApprovedDate: account?.permitApprovedDate?.palmetto
      ? DateTime.fromISO(account?.permitApprovedDate?.palmetto).toUTC().toFormat(DATE_FORMAT)
      : undefined,
    permitSubmittedDate: account?.permitSubmittedDate?.palmetto
      ? DateTime.fromISO(account?.permitSubmittedDate?.palmetto).toUTC().toFormat(DATE_FORMAT)
      : undefined,
    installScheduledDate: account?.installScheduledDate?.palmetto
      ? DateTime.fromISO(account?.installScheduledDate?.palmetto).toUTC().toFormat(DATE_FORMAT)
      : undefined,
    scheduledInspectionDate: account?.scheduledInspectionDate?.palmetto
      ? DateTime.fromISO(account?.scheduledInspectionDate?.palmetto).toUTC().toFormat(DATE_FORMAT)
      : undefined,
    expectedPTODate: account?.expectedPTODate?.palmetto
      ? DateTime.fromISO(account?.expectedPTODate?.palmetto).toUTC().toFormat(DATE_FORMAT)
      : undefined,
  };

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }
  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.installPackage} />
      <MainContainer>
        <Formik
          initialValues={initialValues}
          validationSchema={energyBackupInstallPackageSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnChange={false}
          validate={handleValidation}
        >
          <EnhancedFormikForm>
            <ActionsList
              milestoneType={MilestoneType.install}
              accountId={queue?.account?.id}
              canSubmitPackage={canSubmitInstallPackage && allowsAction}
              isLoading={isLoading || isSaving || false}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview && allowsAction}
              onSave={handleSave}
              disabled={
                queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !allowsAction || !canEditPackage
              }
              disableApproval={!account?.existingSystemDetails}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              queue={queue}
              subQueueInfo={{
                type: 'Existing System' as QueueType,
                status: account?.existingSystemDetails ? QueueStatus.approved : QueueStatus.submitted,
              }}
            />

            {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
              <QueueBanner
                setIsNotesOpen={setIsNotesOpen}
                queueType={QueueType.installPackage}
                isFinCoUser={isFinCoUser}
                is8090Project={Boolean(queue?.aiPackageReviewStatus)}
              />
            ) : null}
            {action === ReviewPackageActions.review && !canSubmitReview ? (
              <Alert
                variant="warning"
                title="Install Package Not Submitted"
                message="This install package is not ready to be reviewed yet. It has not been submitted."
                hasIcon
              />
            ) : null}
            {showUnderReviewAlert ? (
              <Alert
                variant="warning"
                title="Install Package In Review Queue"
                message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                hasIcon
              />
            ) : null}
            <FormSection
              title="Existing System"
              description={
                account?.existingSystemDetails ? (
                  <Box childGap="xs" display="block" width="100">
                    <Box>Need to update the Existing System details?</Box>
                    <Button
                      onClick={() => {
                        navigate(`/accounts/${account.id}/verify-existing-system`);
                      }}
                      size="sm"
                      variant="secondary"
                      isDisabled={!canEditPackage}
                    >
                      Update Existing System
                    </Button>
                  </Box>
                ) : undefined
              }
            >
              {!account?.existingSystemDetails ? (
                <Box>No existing system details provided.</Box>
              ) : (
                <Box direction="column" gap="sm">
                  <Box direction="row" gap="xs">
                    <strong>Has Existing System:</strong>{' '}
                    {account?.existingSystemDetails?.hasExistingSystem ? 'Yes' : 'No'}
                  </Box>
                  {account?.existingSystemDetails?.hasExistingSystem && (
                    <>
                      <Box direction="row" gap="xs">
                        <strong>AC System Size (kW):</strong> {account?.existingSystemDetails?.acSystemSize || '--'}
                      </Box>
                      <Box direction="row" gap="xs">
                        <strong>Finance Type:</strong>{' '}
                        {FINANCE_TYPE_LABEL[
                          account?.existingSystemDetails?.financeType as EnergyBackupExistingSystemFinanceType
                        ] || '--'}
                      </Box>
                      <DocumentList
                        title="Existing Solar System Documents"
                        documentTypeFilter={[VerifyExistingSystemDocumentTypes.existingSystemPhotos]}
                        allowArchive={false}
                        showUploadButton={false}
                        showTypeLabelOnItems={false}
                        baseCategory="Existing System Photos"
                        isPackageEditable={canEditPackage}
                      />
                      <FlagBox
                        dataKey="Energy Backup Existing Solar System"
                        flagType={RequirementFlagSettingsType.existingSystemEnergyBackup}
                      />
                    </>
                  )}
                </Box>
              )}
            </FormSection>
            <FieldArray name="modelSelections">
              {({ form }) => (
                <FormSection title={'System Details'}>
                  {form.values.modelSelections && form.values.modelSelections.length > 0 ? (
                    form.values.modelSelections.map((selection: any, index: number) => (
                      <Box key={index} gap="sm" direction="row">
                        <Box gap="xs" width="3xl">
                          <Box direction="row" justifyContent="space-between" alignItems="center">
                            <strong>{EquipmentTypeLabels[selection.type as EquipmentType]}</strong>
                          </Box>
                          <Box>
                            {selection.manufacturer} {selection.modelName}
                          </Box>
                        </Box>
                        {selection.modelNumber ? (
                          <Box justifyContent="center" direction="row" gap="sm" alignItems="center">
                            <Box>{selection.modelNumber}</Box>
                            <Button
                              iconPrefix="remove"
                              variant="tertiary"
                              tone="danger"
                              aria-label="Clear model number"
                              size="sm"
                              isDisabled={!canEditPackage}
                              onClick={() => {
                                form.setFieldValue(`modelSelections.${index}.modelNumber`, undefined);
                              }}
                            >
                              Clear
                            </Button>
                          </Box>
                        ) : (
                          <AsyncSelectInput
                            id={`modelSelections.${index}.modelNumber`}
                            name={`modelSelections.${index}.modelNumber`}
                            label={`Search ${EquipmentTypeLabels[selection.type as EquipmentType]}`}
                            loadOnOpen
                            loadOptions={async (input: string) => {
                              const options = await debouncedSearch(
                                input,
                                selection.modelName,
                                selection.manufacturer,
                              );
                              return options;
                            }}
                            isDisabled={!canEditPackage}
                            onChange={(event) => {
                              if (event?.target?.value && event.target.value.value) {
                                form.setFieldValue(
                                  `modelSelections.${index}.modelNumber`,
                                  event?.target?.value?.model,
                                );
                              }
                            }}
                            onBlur={form.handleBlur}
                            isClearable
                            value={''}
                            placeholder="Search by Model or Name..."
                            noOptionsMessage={({ inputValue }: { inputValue: string | React.JSX.Element }) => {
                              const equipmentLabel = EquipmentTypeLabels[selection.type as EquipmentType];

                              if (!inputValue) return `Type to search for ${equipmentLabel}...`;
                              return `No ${equipmentLabel} found`;
                            }}
                          />
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box>No equipment added.</Box>
                  )}
                  <FlagBox
                    dataKey="System Design Issue"
                    flagType={RequirementFlagSettingsType.systemDesignIssueEnergyBackup}
                  />
                </FormSection>
              )}
            </FieldArray>
            <FormSection
              title="Monitoring & System Settings"
              description={
                <Box childGap="xs" color="body-secondary">
                  <p>
                    <b>Required:</b> Monitoring portal ID for the battery.
                  </p>
                  <p>Ensure site access is shared to Palmetto as owner & CTs are configured.</p>
                  <p>
                    <TextLink
                      href="https://help.palmetto.finance/en/articles/12277578-energy-backup-m1-m2-requirements"
                      target="_blank"
                      rel="noreferrer"
                    >
                      See monitoring portal access requirements
                    </TextLink>
                  </p>
                </Box>
              }
            >
              <FlagBox
                dataKey="Battery Monitoring ID"
                flagType={RequirementFlagSettingsType.batteryMonitoringId}
                displayWithNoQueue
              >
                <Box
                  direction={{ base: 'column', tablet: 'row' }}
                  alignItems="center"
                  childGap={{ base: 'lg', tablet: '3xl' }}
                  width="100"
                >
                  <Field
                    type="text"
                    name="batteryMonitoringSiteId"
                    id="batteryMonitoringSiteId"
                    hideLabel
                    isRequired
                    component={FormikTextInput}
                    isDisabled={!canEditPackage}
                  />
                </Box>
              </FlagBox>

              <Box gap="md" margin={'md 0 0 0'}>
                <FlagBox
                  dataKey="Access to LightReach - Battery"
                  flagType={RequirementFlagSettingsType.accessToLightReachBattery}
                />
                <FlagBox
                  dataKey="Configuration and Settings - Battery"
                  flagType={RequirementFlagSettingsType.configurationSettingsBattery}
                />
              </Box>
            </FormSection>
            <Permit
              allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
              showCheckboxes={isFinCoUser}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <UtilityBill
              account={account}
              originContext={QueueType.installPackage}
              allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
              showCheckboxes={isFinCoUser}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <ApprovedInterconnectionAgreement
              account={account}
              allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
              showCheckboxes={isFinCoUser}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <FormSection
              title="Plan Set"
              description="The plan set must accurately reflect the installed equipment on site."
            >
              <DocumentList
                documentTypeFilter={[InstallDocumentTypes.planSet]}
                title="Plan Set"
                baseCategory={'Plan Set'}
                allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                showCheckboxes={isFinCoUser}
                checkboxCategories={checkboxCategories}
                isPackageEditable={canEditPackage}
              />
            </FormSection>
            <FormSection
              title="All Installation Documentation"
              description={
                <Box gap="sm">
                  <Box display="block">
                    For a full list of requirements,{' '}
                    <TextLink
                      href="https://eb.palmetto.finance/en/articles/12277578-energy-backup-m1-m2-requirements"
                      target="_blank"
                      rel="noreferrer"
                    >
                      check the Knowledge Base.
                    </TextLink>
                  </Box>
                </Box>
              }
            >
              <PhotosList
                title="Uploaded Install Documentation"
                baseCategory={'Energy Backup Install Photos'}
                allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                category={InstallationPackagePhotoCategory.energyBackupInstallationPhotos}
                flagType={RequirementFlagSettingsType.energyBackupInstallationPhotos}
                showCheckboxes={isFinCoUser}
                programType={ProgramType.energyBackup}
                checkboxCategories={checkboxCategories}
                isPackageEditable={canEditPackage}
              />
            </FormSection>
            <Footer
              canSubmitPackage={canSubmitInstallPackage && allowsAction}
              disableAllApprovals={!account?.existingSystemDetails}
              isLoading={isLoading || isSaving}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview && allowsAction}
              onSave={handleSave}
              disabled={
                queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !allowsAction || !canEditPackage
              }
            />
          </EnhancedFormikForm>
        </Formik>
      </MainContainer>
    </QueueFlagsProvider>
  );
};
